import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Download, FileText, FileSpreadsheet, ArrowUpDown, ArrowUp, ArrowDown, LayoutTemplate } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Column definitions per export type
export const EXPORT_COLUMNS = {
  clients: [
    { key: "name", label: "Nome", default: true },
    { key: "client_type", label: "Tipo", default: true },
    { key: "fiscal_code", label: "CF/P.IVA", default: true },
    { key: "email", label: "Email", default: true },
    { key: "phone", label: "Telefono", default: true },
    { key: "city", label: "Citta", default: true },
    { key: "policy_count", label: "N. Polizze", default: true },
    { key: "total_premium", label: "Totale Premi", default: true },
    { key: "address", label: "Indirizzo", default: false },
    { key: "cap", label: "CAP", default: false },
    { key: "province", label: "Provincia", default: false },
    { key: "pec", label: "PEC", default: false },
    { key: "birth_date", label: "Data Nascita", default: false },
    { key: "notes", label: "Note", default: false },
  ],
  policies: [
    { key: "client_name", label: "Cliente", default: true },
    { key: "company", label: "Compagnia", default: true },
    { key: "policy_type", label: "Tipo", default: true },
    { key: "expiry_date", label: "Scadenza", default: true },
    { key: "premium", label: "Premio", default: true },
    { key: "consultation_fee", label: "Consulenza", default: true },
    { key: "total", label: "Totale", default: true },
    { key: "status", label: "Stato", default: true },
    { key: "policy_number", label: "N. Polizza", default: false },
    { key: "license_plate", label: "Targa", default: false },
    { key: "payment_frequency", label: "Frequenza Pag.", default: false },
    { key: "start_date", label: "Data Inizio", default: false },
    { key: "notes", label: "Note", default: false },
  ],
  commissions: [
    { key: "client_name", label: "Cliente", default: true },
    { key: "company", label: "Compagnia", default: true },
    { key: "policy_type", label: "Tipo", default: true },
    { key: "expiry_date", label: "Scadenza", default: true },
    { key: "semiannual_expiry", label: "Scad. Semestrale", default: true },
    { key: "premium", label: "Premio", default: true },
    { key: "consultation_fee", label: "Consulenza", default: true },
    { key: "total", label: "Totale", default: true },
    { key: "renewed", label: "Rinnovata", default: true },
    { key: "start_date", label: "Decorrenza", default: false },
    { key: "policy_number", label: "N. Polizza", default: false },
    { key: "license_plate", label: "Targa", default: false },
    { key: "payment_frequency", label: "Frequenza Pag.", default: false },
    { key: "notes_blank", label: "Note (vuota)", default: false },
  ],
  expiring: [
    { key: "display_date", label: "Data", default: true },
    { key: "policy_type", label: "Tipo", default: true },
    { key: "client_name", label: "Cliente", default: true },
    { key: "client_phone", label: "Telefono", default: true },
    { key: "company", label: "Compagnia", default: true },
    { key: "license_plate", label: "Targa", default: true },
    { key: "premium", label: "Premio", default: true },
    { key: "stato_rinnovo", label: "Stato", default: true },
    { key: "policy_number", label: "N. Polizza", default: false },
    { key: "client_email", label: "Email Cliente", default: false },
  ],
  calendar: [
    { key: "date", label: "Data", default: true },
    { key: "type", label: "Tipo", default: true },
    { key: "client", label: "Cliente", default: true },
    { key: "phone", label: "Telefono", default: true },
    { key: "policy", label: "Polizza", default: true },
    { key: "company", label: "Compagnia", default: true },
    { key: "premium", label: "Premio", default: true },
    { key: "email", label: "Email Cliente", default: false },
    { key: "license_plate", label: "Targa", default: false },
  ],
};

export function ExportColumnsDialog({ open, onOpenChange, exportType, onExport, exporting = false, children = null }) {
  const columns = EXPORT_COLUMNS[exportType] || [];
  const [selected, setSelected] = useState([]);
  const [format, setFormat] = useState("pdf");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [landscape, setLandscape] = useState(false);
  const [includeArrears, setIncludeArrears] = useState(true);

  useEffect(() => {
    if (open) {
      setSelected(columns.filter(c => c.default).map(c => c.key));
      setSortBy("");
      setSortDir("asc");
      setIncludeArrears(true);
    }
  }, [open, exportType]);

  const toggleColumn = (key) => {
    setSelected(prev => {
      const newSelected = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      if (sortBy === key && !newSelected.includes(key)) setSortBy("");
      return newSelected;
    });
  };

  const selectAll = () => setSelected(columns.map(c => c.key));
  const selectDefaults = () => {
    const defaults = columns.filter(c => c.default).map(c => c.key);
    setSelected(defaults);
    if (sortBy && !defaults.includes(sortBy)) setSortBy("");
  };

  const handleExport = () => {
    if (selected.length === 0) return;
    onExport(format, selected, sortBy || null, sortDir, landscape, includeArrears);
  };

  const selectedColumns = columns.filter(c => selected.includes(c.key));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="export-columns-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Configura Export
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Format selector */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Formato</Label>
            <div className="flex gap-2">
              <Button
                variant={format === "pdf" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                onClick={() => setFormat("pdf")}
                data-testid="export-format-pdf"
              >
                <FileText className="mr-2 h-4 w-4" />
                PDF
              </Button>
              <Button
                variant={format === "excel" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                onClick={() => setFormat("excel")}
                data-testid="export-format-excel"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Excel
              </Button>
            </div>
          </div>

          {/* Opzioni proprie della pagina (es. periodo e compagnia del report, 21/10/2026) */}
          {children}

          {/* Orientation (PDF only) */}
          {format === "pdf" && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Orientamento</Label>
              <div className="flex gap-2">
                <Button
                  variant={!landscape ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setLandscape(false)}
                  data-testid="export-portrait"
                >
                  <LayoutTemplate className="mr-2 h-4 w-4" />
                  Verticale
                </Button>
                <Button
                  variant={landscape ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setLandscape(true)}
                  data-testid="export-landscape"
                >
                  <LayoutTemplate className="mr-2 h-4 w-4 rotate-90" />
                  Orizzontale
                </Button>
              </div>
            </div>
          )}

          {/* Include Arrears (commissions only) */}
          {exportType === "commissions" && (
            <div className="flex items-center gap-2 p-3 rounded-md border bg-amber-50/50 dark:bg-amber-950/10 border-amber-200">
              <Checkbox
                id="include-arrears"
                checked={includeArrears}
                onCheckedChange={(checked) => setIncludeArrears(!!checked)}
                data-testid="export-include-arrears"
              />
              <Label htmlFor="include-arrears" className="text-sm cursor-pointer font-medium">
                Includi Arretrati del mese
              </Label>
            </div>
          )}

          {/* Columns */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Colonne ({selected.length}/{columns.length})</Label>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs text-primary hover:underline"
                  data-testid="export-select-all"
                >
                  Tutte
                </button>
                <button
                  onClick={selectDefaults}
                  className="text-xs text-muted-foreground hover:underline"
                  data-testid="export-select-defaults"
                >
                  Default
                </button>
              </div>
            </div>

            {/* Default columns */}
            <div className="grid grid-cols-2 gap-2 p-3 rounded-md border bg-muted/30">
              {columns.filter(c => c.default).map(col => (
                <div key={col.key} className="flex items-center gap-2">
                  <Checkbox
                    id={`col-${col.key}`}
                    checked={selected.includes(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    data-testid={`export-col-${col.key}`}
                  />
                  <Label htmlFor={`col-${col.key}`} className="text-sm cursor-pointer">
                    {col.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Extra columns */}
            {columns.filter(c => !c.default).length > 0 && (
              <>
                <Label className="text-xs text-muted-foreground">Campi aggiuntivi</Label>
                <div className="grid grid-cols-2 gap-2 p-3 rounded-md border border-dashed">
                  {columns.filter(c => !c.default).map(col => (
                    <div key={col.key} className="flex items-center gap-2">
                      <Checkbox
                        id={`col-${col.key}`}
                        checked={selected.includes(col.key)}
                        onCheckedChange={() => toggleColumn(col.key)}
                        data-testid={`export-col-${col.key}`}
                      />
                      <Label htmlFor={`col-${col.key}`} className="text-sm cursor-pointer">
                        {col.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Sort options */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <ArrowUpDown className="h-4 w-4" />
              Ordina per
            </Label>
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="flex-1" data-testid="export-sort-column">
                  <SelectValue placeholder="Nessun ordinamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun ordinamento</SelectItem>
                  {selectedColumns.map(col => (
                    <SelectItem key={col.key} value={col.key}>{col.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
                disabled={!sortBy || sortBy === "none"}
                className="shrink-0"
                data-testid="export-sort-direction"
                title={sortDir === "asc" ? "Crescente (A→Z, 1→9)" : "Decrescente (Z→A, 9→1)"}
              >
                {sortDir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {sortDir === "asc" ? "Crescente: A→Z, dal meno al piu" : "Decrescente: Z→A, dal piu al meno"}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="export-cancel-btn">
            Annulla
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || selected.length === 0}
            data-testid="export-confirm-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? "Esportazione..." : "Esporta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
